import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fmtErr, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, PriorityBadge, STATUS_LABELS, PRIORITY_LABELS } from "@/components/badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Eye, Search, MoreHorizontal, Trash2 } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { ConfirmDialog } from "@/components/common";
import { motion } from "framer-motion";

export default function TicketsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState(null);
  const [filters, setFilters] = useState({ status: "", priority: "", category_id: "", company_id: "", q: "", client_id: "", assignee_id: "", date_from: "", date_to: "", view: "" });
  const [categories, setCategories] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [clients, setClients] = useState([]);
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ subject: "", description: "", category_id: "", client_id: "", priority: "" });
  const [statusTarget, setStatusTarget] = useState(null);
  const [assignTarget, setAssignTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [selected, setSelected] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [hardDeleteTarget, setHardDeleteTarget] = useState(null);
  const isSuper = user?.role === "super_admin";
  const canAssign = ["super_admin", "company_admin", "manager"].includes(user?.role);
  const isStaff = user?.role !== "client_user";

  const load = useCallback(() => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    api.get("/tickets", { params }).then(({ data }) => setTickets(data)).catch((e) => toast.error(fmtErr(e)));
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
    if (isSuper) api.get("/companies").then(({ data }) => setCompanies(data)).catch(() => {});
    if (isStaff) {
      api.get("/clients").then(({ data }) => setClients(data)).catch(() => {});
      if (["super_admin", "company_admin"].includes(user?.role)) {
        api.get("/users").then(({ data }) => setStaff(data.filter((u) => u.role !== "super_admin"))).catch(() => {});
      }
    }
  }, []);

  const quickStatus = async (t, status) => {
    try {
      await api.patch(`/tickets/${t.id}/status`, { status });
      toast.success(status === "cloture" ? "Ticket clôturé" : "Ticket rouvert");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const saveStatus = async () => {
    try {
      await api.patch(`/tickets/${statusTarget.id}/status`, { status: statusTarget.newStatus });
      toast.success("Statut mis à jour");
      setStatusTarget(null);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const saveAssign = async () => {
    try {
      await api.patch(`/tickets/${assignTarget.id}/assign`, { assignee_id: assignTarget.newAssignee === "none" ? null : assignTarget.newAssignee });
      toast.success("Ticket réassigné");
      setAssignTarget(null);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const doDelete = async () => {
    try {
      await api.post(`/tickets/${deleteTarget.id}/delete`, { reason: deleteReason || null });
      toast.success("Ticket déplacé dans la corbeille");
      setDeleteTarget(null);
      setDeleteReason("");
      load();
    } catch (e) { toast.error(fmtErr(e)); throw e; }
  };

  const toggleOne = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const visibleIds = (tickets || []).map((t) => t.id);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.includes(id));
  const toggleAll = () => setSelected(allSelected ? [] : visibleIds);

  const doHardDelete = async () => {
    try {
      await api.delete(`/tickets/${hardDeleteTarget.id}`);
      toast.success(`Ticket ${hardDeleteTarget.number} supprimé définitivement`);
      setSelected((s) => s.filter((x) => x !== hardDeleteTarget.id));
      setHardDeleteTarget(null);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const doBulkDelete = async () => {
    try {
      const { data } = await api.post("/tickets/bulk-delete", { ids: selected });
      toast.success(`${data.deleted} ticket(s) supprimé(s) définitivement`);
      setSelected([]);
      setBulkOpen(false);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/tickets", {
        subject: form.subject, description: form.description,
        category_id: form.category_id || null, client_id: form.client_id,
        priority: form.priority || null,
      });
      setOpen(false);
      setForm({ subject: "", description: "", category_id: "", client_id: "", priority: "" });
      load();
      toast.success("Ticket créé — notification enregistrée (simulation)");
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setSaving(false);
    }
  };

  const basePath = isStaff ? "/app/tickets" : "/portal/tickets";

  return (
    <div data-testid="tickets-page">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Tickets</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {isSuper ? "Tous les tickets de toutes les sociétés." : user?.role === "collaborator" ? "Tickets qui vous sont assignés." : "Tickets de votre société."}
          </p>
        </div>
        {isStaff && user?.role !== "collaborator" && (
          <Button data-testid="create-ticket-button" onClick={() => setOpen(true)} className="rounded-sm bg-primary text-white hover:bg-red-700">
            <Plus className="mr-2 h-4 w-4" /> Nouveau ticket
          </Button>
        )}
      </div>

      <div className="mb-5 flex flex-wrap gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="search-tickets" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            placeholder="N° ou sujet…" className="w-56 rounded-sm pl-9" />
        </div>
        <Select value={filters.view} onValueChange={(v) => setFilters({ ...filters, view: v === "all" ? "" : v })}>
          <SelectTrigger data-testid="filter-view" className="w-44 rounded-sm"><SelectValue placeholder="Vue : toutes" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Vue : toutes</SelectItem>
            <SelectItem value="open">Tickets ouverts</SelectItem>
            <SelectItem value="closed">Tickets clôturés</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
          <SelectTrigger data-testid="filter-status" className="w-48 rounded-sm"><SelectValue placeholder="Statut : tous" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Statut : tous</SelectItem>
            {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.priority} onValueChange={(v) => setFilters({ ...filters, priority: v === "all" ? "" : v })}>
          <SelectTrigger data-testid="filter-priority" className="w-44 rounded-sm"><SelectValue placeholder="Priorité : toutes" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Priorité : toutes</SelectItem>
            {Object.entries(PRIORITY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.category_id} onValueChange={(v) => setFilters({ ...filters, category_id: v === "all" ? "" : v })}>
          <SelectTrigger data-testid="filter-category" className="w-52 rounded-sm"><SelectValue placeholder="Catégorie : toutes" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Catégorie : toutes</SelectItem>
            {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {isStaff && (
          <Select value={filters.client_id} onValueChange={(v) => setFilters({ ...filters, client_id: v === "all" ? "" : v })}>
            <SelectTrigger data-testid="filter-client" className="w-52 rounded-sm"><SelectValue placeholder="Client : tous" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Client : tous</SelectItem>
              {clients.map((c) => <SelectItem key={c.id} value={c.id}>{isSuper ? `${c.name} (${c.company_name})` : c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {canAssign && (
          <Select value={filters.assignee_id} onValueChange={(v) => setFilters({ ...filters, assignee_id: v === "all" ? "" : v })}>
            <SelectTrigger data-testid="filter-assignee" className="w-48 rounded-sm"><SelectValue placeholder="Assigné : tous" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Assigné : tous</SelectItem>
              {staff.map((u) => <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {isSuper && (
          <Select value={filters.company_id} onValueChange={(v) => setFilters({ ...filters, company_id: v === "all" ? "" : v })}>
            <SelectTrigger data-testid="filter-company" className="w-48 rounded-sm"><SelectValue placeholder="Société : toutes" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Société : toutes</SelectItem>
              {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Input type="date" data-testid="filter-date-from" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="w-40 rounded-sm" title="Créé à partir du" />
        <Input type="date" data-testid="filter-date-to" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="w-40 rounded-sm" title="Créé jusqu'au" />
      </div>

      {isSuper && selected.length > 0 && (
        <div data-testid="tickets-bulk-bar" className="mb-3 flex items-center justify-between rounded-sm border border-primary/40 bg-primary/5 px-4 py-2.5">
          <span className="text-sm font-medium" data-testid="tickets-bulk-count">{selected.length} ticket(s) sélectionné(s)</span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="rounded-sm" onClick={() => setSelected([])} data-testid="tickets-bulk-clear">Annuler</Button>
            <Button size="sm" className="rounded-sm bg-primary text-white hover:bg-red-700" onClick={() => setBulkOpen(true)} data-testid="tickets-bulk-delete-button">
              <Trash2 className="mr-2 h-4 w-4" /> Supprimer la sélection
            </Button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto rounded-sm border border-border bg-card">
        <table className="w-full text-sm" data-testid="tickets-table">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              {isSuper && (
                <th className="px-5 py-3 w-10">
                  <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="tickets-select-all" aria-label="Tout sélectionner" />
                </th>
              )}
              <th className="px-5 py-3 font-semibold">N°</th>
              <th className="px-5 py-3 font-semibold">Sujet</th>
              {isSuper && <th className="px-5 py-3 font-semibold">Société</th>}
              <th className="px-5 py-3 font-semibold">Client</th>
              <th className="px-5 py-3 font-semibold">Catégorie</th>
              <th className="px-5 py-3 font-semibold">Assigné à</th>
              <th className="px-5 py-3 font-semibold">Statut</th>
              <th className="px-5 py-3 font-semibold">Priorité</th>
              <th className="px-5 py-3 font-semibold">Créé le</th>
              <th className="px-5 py-3 font-semibold"></th>
            </tr>
          </thead>
          <tbody>
            {(tickets || []).map((t, i) => (
              <motion.tr key={t.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.02 }}
                className="cursor-pointer border-b border-border last:border-b-0 hover:bg-accent transition-colors duration-200"
                data-testid={`ticket-row-${t.number}`}
                onClick={() => navigate(`${basePath}/${t.id}`)}>
                {isSuper && (
                  <td className="px-5 py-3" onClick={(e) => e.stopPropagation()}>
                    <Checkbox checked={selected.includes(t.id)} onCheckedChange={() => toggleOne(t.id)}
                      data-testid={`ticket-select-${t.number}`} aria-label={`Sélectionner ${t.number}`} />
                  </td>
                )}
                <td className="px-5 py-3">
                  <Link to={`${basePath}/${t.id}`} data-testid={`ticket-number-${t.number}`} className="font-mono text-xs font-semibold text-primary" onClick={(e) => e.stopPropagation()}>
                    {t.number}
                  </Link>
                </td>
                <td className="max-w-64 truncate px-5 py-3 font-medium">{t.subject}</td>
                {isSuper && <td className="px-5 py-3 text-muted-foreground">{t.company_name}</td>}
                <td className="px-5 py-3">{t.client_name}</td>
                <td className="px-5 py-3">
                  {t.category_name && (
                    <span className="inline-flex items-center gap-1.5 text-xs">
                      <span className="h-2 w-2 rounded-full" style={{ background: t.category_color || "#666" }} />{t.category_name}
                    </span>
                  )}
                </td>
                <td className="px-5 py-3 text-muted-foreground">{t.assignee_name || "—"}</td>
                <td className="px-5 py-3"><StatusBadge status={t.status} /></td>
                <td className="px-5 py-3"><PriorityBadge priority={t.priority} /></td>
                <td className="px-5 py-3 text-xs text-muted-foreground">{fmtDateTime(t.created_at)}</td>
                <td className="px-5 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Link to={`${basePath}/${t.id}`} data-testid={`ticket-detail-${t.number}`} onClick={(e) => e.stopPropagation()}
                      className="inline-flex rounded-sm border border-border p-1.5 text-muted-foreground hover:text-primary transition-colors" title="Ouvrir le ticket">
                      <Eye className="h-3.5 w-3.5" />
                    </Link>
                    {isStaff && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button data-testid={`ticket-actions-${t.number}`} onClick={(e) => e.stopPropagation()}
                            className="rounded-sm border border-border p-1.5 text-muted-foreground hover:text-foreground transition-colors">
                            <MoreHorizontal className="h-3.5 w-3.5" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="rounded-sm">
                          <DropdownMenuItem asChild data-testid={`ticket-action-open-${t.number}`}>
                            <Link to={`${basePath}/${t.id}`}>Ouvrir</Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem data-testid={`ticket-action-status-${t.number}`} onClick={(e) => { e.stopPropagation(); setStatusTarget({ ...t, newStatus: t.status }); }}>Changer le statut</DropdownMenuItem>
                          {canAssign && (
                            <DropdownMenuItem data-testid={`ticket-action-assign-${t.number}`} onClick={(e) => { e.stopPropagation(); setAssignTarget({ ...t, newAssignee: t.assignee_id || "none" }); }}>Réassigner</DropdownMenuItem>
                          )}
                          {t.status !== "cloture" ? (
                            <DropdownMenuItem data-testid={`ticket-action-close-${t.number}`} onClick={(e) => { e.stopPropagation(); quickStatus(t, "cloture"); }}>Clôturer</DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem data-testid={`ticket-action-reopen-${t.number}`} onClick={(e) => { e.stopPropagation(); quickStatus(t, "rouvert"); }}>Rouvrir</DropdownMenuItem>
                          )}
                          {isSuper && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem data-testid={`ticket-action-delete-${t.number}`} onClick={(e) => { e.stopPropagation(); setDeleteTarget(t); }} className="text-muted-foreground">
                                Supprimer (corbeille)
                              </DropdownMenuItem>
                              <DropdownMenuItem data-testid={`ticket-action-harddelete-${t.number}`} onClick={(e) => { e.stopPropagation(); setHardDeleteTarget(t); }} className="text-primary font-medium">
                                <Trash2 className="mr-2 h-4 w-4" /> Supprimer définitivement
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
        {tickets && tickets.length === 0 && <div className="p-10 text-center text-sm text-muted-foreground">Aucun ticket avec ces filtres.</div>}
      </div>

      <Dialog open={!!statusTarget} onOpenChange={() => setStatusTarget(null)}>
        <DialogContent className="rounded-sm" data-testid="status-dialog">
          <DialogHeader>
            <DialogTitle>Changer le statut — {statusTarget?.number}</DialogTitle>
          </DialogHeader>
          {statusTarget && (
            <div className="space-y-4">
              <Select value={statusTarget.newStatus} onValueChange={(v) => setStatusTarget({ ...statusTarget, newStatus: v })}>
                <SelectTrigger data-testid="status-dialog-select" className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
              <DialogFooter>
                <Button data-testid="status-dialog-submit" onClick={saveStatus} className="rounded-sm bg-primary text-white hover:bg-red-700">Appliquer</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!assignTarget} onOpenChange={() => setAssignTarget(null)}>
        <DialogContent className="rounded-sm" data-testid="assign-dialog">
          <DialogHeader>
            <DialogTitle>Réassigner — {assignTarget?.number}</DialogTitle>
          </DialogHeader>
          {assignTarget && (
            <div className="space-y-4">
              <Select value={assignTarget.newAssignee} onValueChange={(v) => setAssignTarget({ ...assignTarget, newAssignee: v })}>
                <SelectTrigger data-testid="assign-dialog-select" className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Non assigné</SelectItem>
                  {staff.map((u) => <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>)}
                </SelectContent>
              </Select>
              <DialogFooter>
                <Button data-testid="assign-dialog-submit" onClick={saveAssign} className="rounded-sm bg-primary text-white hover:bg-red-700">Réassigner</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent className="rounded-sm" data-testid="delete-ticket-dialog">
          <DialogHeader>
            <DialogTitle>Supprimer le ticket {deleteTarget?.number} ?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Êtes-vous sûr de vouloir supprimer ce ticket ? Il sera déplacé dans la corbeille (réservé Super Admin) et masqué des listes. Les données restent conservées. Action journalisée.
          </p>
          <div className="space-y-2">
            <Label>Motif (optionnel)</Label>
            <Input data-testid="delete-ticket-reason" value={deleteReason} onChange={(e) => setDeleteReason(e.target.value)} className="rounded-sm" placeholder="Ex : doublon, spam…" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} className="rounded-sm">Annuler</Button>
            <Button data-testid="delete-ticket-confirm" onClick={doDelete} className="rounded-sm bg-primary text-white hover:bg-red-700">Supprimer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!hardDeleteTarget} onOpenChange={() => setHardDeleteTarget(null)}
        title={`Supprimer définitivement le ticket ${hardDeleteTarget?.number} ?`}
        description="Cette action est IRRÉVERSIBLE. Le ticket et TOUS ses messages (chat), fichiers et notifications associés seront définitivement supprimés de la base. Action journalisée."
        confirmLabel="Supprimer définitivement" onConfirm={doHardDelete} testid="harddelete-ticket-dialog" />

      <ConfirmDialog open={bulkOpen} onOpenChange={setBulkOpen}
        title={`Supprimer ${selected.length} ticket(s) définitivement ?`}
        description="Cette action est IRRÉVERSIBLE. Chaque ticket sélectionné et TOUS ses messages (chat), fichiers et notifications seront définitivement supprimés de la base. Action journalisée."
        confirmLabel="Supprimer la sélection" onConfirm={doBulkDelete} testid="bulk-delete-tickets-dialog" />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm sm:max-w-lg" data-testid="create-ticket-dialog">
          <DialogHeader>
            <DialogTitle>Nouveau ticket</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label>Client entreprise *</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger data-testid="ticket-client-select" className="rounded-sm"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                <SelectContent>
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{isSuper ? `${c.name} (${c.company_name})` : c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Catégorie</Label>
              <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="ticket-category-select" className="rounded-sm"><SelectValue placeholder="Aucune" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucune</SelectItem>
                  {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Sujet *</Label>
              <Input data-testid="ticket-subject-input" required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea data-testid="ticket-description-input" required rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm" />
            </div>
            <DialogFooter>
              <Button data-testid="ticket-submit-button" type="submit" disabled={saving || !form.client_id} className="rounded-sm bg-primary text-white hover:bg-red-700">
                {saving ? "Création…" : "Créer le ticket"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
